import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Route } from "react-router-dom";
import debounce from "lodash.debounce";
import { SearchContainer } from "@arcteryx/container-search";
import DataLayer from "@arcteryx/data-layer";
import Config from "./config";

import i18n from "../i18n";
import { FeatureSet } from "../common/features";
import {
  searchAnalyticsEvent,
  listingViewedAnalyticsEvent,
  ANALYTICS_DEBOUNCE_MS,
  linkClickedAnalyticsEvent,
} from "../src/components/Analytics";
import { getPersonalizedPriceProvider } from "./utils/getPersonalizedPriceProvider";
import { buildProductModel } from "../src/components/AnalyticsHelpers";

// i18n init
import "../i18n";
import { fetchSanityStaticSearchLinksSettings } from "../src/components/GeneralHelpers";
import { getCmsUrl } from "./utils/getCmsUrl";

export const InitSearch = async (searchIsAlreadyOpen, renderElement, indexConstructor) => {
  const { market, language, country } = indexConstructor;
  const personalizedPriceProvider = getPersonalizedPriceProvider({ market, language, country });
  // use algolia env feature toggle to set the env for the index
  const isPreprod = FeatureSet.useAlgoliaPreprod();
  const environment = isPreprod ? "preprod" : "prod";
  // create a new object with the environment and pass it into SearchContainer
  const stateFromHeader = {
    ...indexConstructor,
    environment,
  };

  if (searchIsAlreadyOpen) {
    ReactDOM.unmountComponentAtNode(renderElement);
    return null;
  }

  // Sets the language for i18n
  if (language && i18n.language !== language) {
    i18n.changeLanguage(language);
  }

  const handleListingViewedEvent = debounce((results, resetSearch = false) => {
    listingViewedAnalyticsEvent(results, results._state, resetSearch, country, language);
  }, ANALYTICS_DEBOUNCE_MS);

  const handleSearchEvent = debounce(props => {
    const { searchTermEntered } = props;
    if (searchTermEntered) {
      searchAnalyticsEvent(props);
    }
  }, ANALYTICS_DEBOUNCE_MS);

  const handleClickProductEvent = (data, index) => {
    DataLayer.Event.listingItemClicked(
      new DataLayer.Data.ListingProduct(buildProductModel(data, country, language), index)
    );
  };

  const handleLinkClickedEvent = link => {
    linkClickedAnalyticsEvent(link);
  };

  const analyticsEvents = {
    handleSearchEvent,
    handleListingViewedEvent,
    handleClickProductEvent,
    handleLinkClickedEvent,
  };

  let sanityData = [];
  const fetchStaticSearchLinksSettings = async () => {
    try {
      const siteSettingsStaticSearchLinks = await fetchSanityStaticSearchLinksSettings(market, country, language);
      sanityData = siteSettingsStaticSearchLinks?.staticSearchLinks?.staticSearchLinks?.header || [];
    } catch (error) {
      console.error("Error fetching static search links:", error);
    }
  };

  await fetchStaticSearchLinksSettings();

  const CMS = getCmsUrl(market, country, language);
  // Check if the URL search query param contains any HTML tags
  const searchContainsHtmlTags = decodeURIComponent(window.location.search).match(/<[^>]+>/gi);
  if (searchContainsHtmlTags) {
    return null;
  }

  ReactDOM.render(
    <Router>
      <Route
        path="/"
        component={() => (
          <SearchContainer
            mountNode={renderElement}
            stateFromHeader={stateFromHeader}
            algoliaAppId={Config.ALGOLIA_APP_ID}
            algoliaApiKey={Config.ALGOLIA_API_KEY}
            searchDebounceTime={Config.SEARCH_DEBOUNCE_TIME}
            analyticsEvents={analyticsEvents}
            personalizedPriceProvider={personalizedPriceProvider}
            deployEnv={DEPLOY_ENV}
            cmsUrl={CMS}
            enableBadges={Config.ENABLE_BADGES}
            cmsStaticSearchLinks={sanityData}
          />
        )}
      />
    </Router>,
    renderElement
  );
};
